import {
  Button,
  ButtonGroup,
  Divider,
  Modal,
  useMantineTheme,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import React, { useState } from "react";
import { HiFilter, HiX } from "react-icons/hi";
import "./TimeRangeFilter.css";

export default function TimeRangeFilter(props: {
  onChange?: (start: Date, end: Date) => void;
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const [opened, { open, close }] = useDisclosure(false);
  const [value, setValue] = useState<[string | null, string | null]>([
    null,
    null,
  ]);
  const [modalValue, setModalValue] = useState<[string | null, string | null]>([
    null,
    null,
  ]);

  const formatDateRange = (date: string, date2: string, short = false) => {
    if (date === date2) return date;
    if (!date || !date2) return " ";

    const dateObj = new Date(date);
    const date2Obj = new Date(date2);

    const mapDateLong = (date: Date) => {
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const mapDateShort = (date: Date) => {
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    const day1 = short ? mapDateShort(dateObj) : mapDateLong(dateObj);
    const day2 = short ? mapDateShort(date2Obj) : mapDateLong(date2Obj);

    return `${day1} - ${day2}`;
  };

  const onApplyClick = () => {
    setValue([...modalValue]);
    if (props.onChange) {
      props.onChange(new Date(modalValue[0]), new Date(modalValue[1]));
    }
    close();
  };

  const clearFilter = () => {
    setValue([null, null]);
    setModalValue([null, null]);
    props.onChange?.(null, null);
    close();
  };

  const buildPresets = () => {
    if (isMobile) {
      //return undefined;
    }
    return [
      {
        value: [
          dayjs().subtract(7, "day").format("YYYY-MM-DD"),
          dayjs().format("YYYY-MM-DD"),
        ],
        label: "Last 7 days",
      },
      {
        value: [
          dayjs().startOf("month").format("YYYY-MM-DD"),
          dayjs().format("YYYY-MM-DD"),
        ],
        label: "This month",
      },
      {
        value: [
          dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        ],
        label: "Last month",
      },
      {
        value: [
          dayjs().subtract(6, "month").startOf("month").format("YYYY-MM-DD"),
          dayjs().format("YYYY-MM-DD"),
        ],
        label: "Last 6 months",
      },
      {
        value: [
          dayjs().subtract(1, "year").startOf("year").format("YYYY-MM-DD"),
          dayjs().subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
        ],
        label: "Last year",
      },
    ];
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        withCloseButton={true}
        title={
          <div className="flex flex-row gap-2">
            <HiFilter />
            <div>Select a time range</div>
          </div>
        }
      >
        <div
          className={
            "w-full h-full flex flex-col items-center gap-4" +
            (isMobile ? " customDatePicker" : "")
          }
        >
          <DatePicker
            type="range"
            defaultLevel="month"
            value={modalValue}
            onChange={setModalValue}
            presets={buildPresets() as any}
          />
          <div className="w-full flex flex-row justify-between items-center gap-2">
            <div className="flex gap-2">
              {formatDateRange(modalValue[0], modalValue[1])}
            </div>
            <Button
              className="min-w-fit"
              onClick={onApplyClick}
              disabled={!modalValue[0] || !modalValue[1]}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>

      <div className="flex w-full justify-end">
        <ButtonGroup>
          <Button
            variant="light"
            color="gray"
            leftSection={<HiFilter size={16} />}
            onClick={open}
          >
            {value[0] && value[1] ? (
              <div className="flex gap-2 items-center text-wrap">
                <div>{formatDateRange(value[0], value[1], isMobile)}</div>
              </div>
            ) : (
              "All time"
            )}
          </Button>

          {value[0] && value[1] ? (
            <>
              <Divider w={"1px"}></Divider>
              <Button
                variant="light"
                color="gray"
                p={"xs"}
                onClick={clearFilter}
              >
                <HiX size={16} />
              </Button>
            </>
          ) : (
            ""
          )}
        </ButtonGroup>
      </div>
    </>
  );
}
